import Button from 'devextreme-react/button';
import LoadIndicator from 'devextreme-react/load-indicator';
import TabPanel, { Item as TabItem } from 'devextreme-react/tab-panel';
import TextBox from 'devextreme-react/text-box';
import { GridFormPopup, opsGridFormPopupSizeKey } from '@jarvis/platform-ui';
import type { UserGroupMembership } from '../../../../api/clientAccountsUsersAdmin';
import { GRID_STATE_KEY } from './hvgClientsColumns';
import { HvgClientGroupAccessPanel } from './HvgClientGroupAccessPanel';
import {
  HvgClientFormField,
  composeDisplayName,
  hvgClientFormGrid2,
  hvgClientFormLayout,
  hvgClientFormPopupMinWidth,
  hvgClientFormPopupWidth,
  hvgClientUserFormPopupHeight,
  hvgClientUserFormPopupMinHeight
} from './hvgClientFormLayout';
import './hvgClientUserFormPopup.css';

export type GuestInviteFormState = {
  givenName: string;
  surname: string;
  email: string;
  department: string;
  jobTitle: string;
  company: string;
  groupIds: string[];
};

export function emptyGuestInviteForm(): GuestInviteFormState {
  return { givenName: '', surname: '', email: '', department: '', jobTitle: '', company: '', groupIds: [] };
}

export type GuestInviteProgressState = {
  title: string;
  detail: string;
  error: string | null;
  done: boolean;
};

type HvgClientGuestInvitePopupProps = {
  visible: boolean;
  form: GuestInviteFormState;
  formError: string | null;
  saving: boolean;
  progress: GuestInviteProgressState | null;
  groupOptions: UserGroupMembership[];
  loadingGroups: boolean;
  activeTab: number;
  onActiveTabChange: (index: number) => void;
  onClose: () => void;
  onInvite: () => void;
  onFormChange: (patch: Partial<GuestInviteFormState>) => void;
};

export function HvgClientGuestInvitePopup({
  visible,
  form,
  formError,
  saving,
  progress,
  groupOptions,
  loadingGroups,
  activeTab,
  onActiveTabChange,
  onClose,
  onInvite,
  onFormChange
}: HvgClientGuestInvitePopupProps) {
  const computedDisplayName = composeDisplayName(form.givenName, form.surname);

  return (
    <GridFormPopup
      visible={visible}
      title={progress ? 'Creating CE account' : 'New CE Account'}
      onClose={onClose}
      width={hvgClientFormPopupWidth}
      minWidth={hvgClientFormPopupMinWidth}
      preferredHeight={hvgClientUserFormPopupHeight.create}
      minHeight={hvgClientUserFormPopupMinHeight.create}
      sizeStorageKey={opsGridFormPopupSizeKey(`${GRID_STATE_KEY}:guest`)}
      error={progress ? progress.error : formError}
      footer={
        progress ? (
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', width: '100%' }}>
            {progress.done || progress.error ? (
              <Button text="Close" type="default" onClick={onClose} />
            ) : (
              <span style={{ fontSize: 13, color: 'var(--text-muted, #6b7280)' }}>
                Please wait — do not close this window.
              </span>
            )}
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', width: '100%' }}>
            <Button text="Cancel" disabled={saving} onClick={onClose} />
            <Button
              text={saving ? 'Inviting…' : 'Send invitation'}
              type="default"
              disabled={saving || loadingGroups}
              onClick={onInvite}
            />
          </div>
        )
      }
    >
      {progress ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '8px 0', minHeight: 120 }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            {!progress.done && !progress.error ? <LoadIndicator visible height={28} width={28} /> : null}
            <div>
              <div style={{ fontWeight: 600, fontSize: 15 }}>{progress.title}</div>
              <div style={{ marginTop: 6, fontSize: 13, lineHeight: 1.45, color: 'var(--text-muted, #4b5563)' }}>
                {progress.detail}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div style={hvgClientFormLayout}>
          <TabPanel
            className="ops-grid-detail-tab-panel"
            deferRendering={false}
            animationEnabled={false}
            swipeEnabled={false}
            showNavButtons={false}
            selectedIndex={activeTab}
            onSelectedIndexChange={(index) => onActiveTabChange(index)}
          >
            <TabItem title="Account">
              <div className="hvg-user-tab">
                <div className="hvg-user-tab__intro">
                  <div>
                    <div className="hvg-user-tab__title">Guest account</div>
                    <p>Invite an external user by email. Microsoft Entra sends the redemption message.</p>
                  </div>
                  <span className="hvg-user-tab__status">3 required fields</span>
                </div>

                <section className="hvg-user-form-card hvg-user-form-card--accent">
                  <div className="hvg-user-form-card__heading">
                    <span className="dx-icon dx-icon-user" aria-hidden="true" />
                    <div>
                      <div className="hvg-user-form-card__title">Person</div>
                      <p>Display name is generated automatically from first and last name.</p>
                    </div>
                  </div>
                  <div style={hvgClientFormGrid2}>
                    <HvgClientFormField label="First name *">
                      <TextBox
                        value={form.givenName}
                        placeholder="First name"
                        onValueChanged={(e) => onFormChange({ givenName: e.value ?? '' })}
                        width="100%"
                      />
                    </HvgClientFormField>
                    <HvgClientFormField label="Last name *">
                      <TextBox
                        value={form.surname}
                        placeholder="Last name"
                        onValueChanged={(e) => onFormChange({ surname: e.value ?? '' })}
                        width="100%"
                      />
                    </HvgClientFormField>
                    <HvgClientFormField label="Display name" span="full">
                      <TextBox
                        value={computedDisplayName || 'Generated from first and last name'}
                        readOnly
                        width="100%"
                      />
                    </HvgClientFormField>
                  </div>
                </section>

                <section className="hvg-user-form-card">
                  <div className="hvg-user-form-card__heading">
                    <span className="dx-icon dx-icon-email" aria-hidden="true" />
                    <div>
                      <div className="hvg-user-form-card__title">Invitation email</div>
                      <p>Microsoft Entra emails the redemption link to this address.</p>
                    </div>
                  </div>
                  <HvgClientFormField
                    label="Email *"
                    hint="The guest must redeem the invitation before they can sign in."
                  >
                    <TextBox
                      value={form.email}
                      placeholder="guest@external.com"
                      mode="email"
                      onValueChanged={(e) => onFormChange({ email: e.value ?? '' })}
                      width="100%"
                    />
                  </HvgClientFormField>
                </section>
              </div>
            </TabItem>
            <TabItem title="Profile">
              <div className="hvg-user-tab">
                <div className="hvg-user-tab__intro">
                  <div>
                    <div className="hvg-user-tab__title">Client profile</div>
                    <p>Optional organization details included with the invited guest account.</p>
                  </div>
                  <span className="hvg-user-tab__status hvg-user-tab__status--optional">Optional</span>
                </div>

                <section className="hvg-user-form-card">
                  <div className="hvg-user-form-card__heading">
                    <span className="dx-icon dx-icon-home" aria-hidden="true" />
                    <div>
                      <div className="hvg-user-form-card__title">Organization</div>
                      <p>Where this guest works and their role.</p>
                    </div>
                  </div>
                  <div style={hvgClientFormGrid2}>
                    <HvgClientFormField label="Company">
                      <TextBox
                        value={form.company}
                        onValueChanged={(e) => onFormChange({ company: e.value ?? '' })}
                        width="100%"
                      />
                    </HvgClientFormField>
                    <HvgClientFormField label="Department">
                      <TextBox
                        value={form.department}
                        onValueChanged={(e) => onFormChange({ department: e.value ?? '' })}
                        width="100%"
                      />
                    </HvgClientFormField>
                    <HvgClientFormField label="Job title" span="full">
                      <TextBox
                        value={form.jobTitle}
                        onValueChanged={(e) => onFormChange({ jobTitle: e.value ?? '' })}
                        width="100%"
                      />
                    </HvgClientFormField>
                  </div>
                </section>
              </div>
            </TabItem>
            <TabItem title="Access">
              <HvgClientGroupAccessPanel
                groupOptions={groupOptions}
                selectedGroupIds={form.groupIds}
                loading={loadingGroups}
                onChange={(groupIds) => onFormChange({ groupIds })}
                intro="Assign security groups now, or leave empty and update access after the guest accepts the invite."
              />
            </TabItem>
          </TabPanel>
        </div>
      )}
    </GridFormPopup>
  );
}
